import { useState } from 'react';

import { Collapse, Icon, List } from '@atom/mui';
import { UserGroupTreeType } from '@atom/types/userGroups';
import { toggleFromSet } from '@atom/utilities/setUtilities';

import UserGroupTreeNode from './UserGroupTreeNode';

import classes from './policyUserGroupSelection.module.css';

const { ListItemButton, ListItemText } = List;

interface Props {
  userGroupTree: UserGroupTreeType;
  selected: Set<string>;
  onToggle: (userGroup: UserGroupTreeType) => void;
  handleToggleDescendants: (
    userGroup: UserGroupTreeType,
    selectAll: boolean,
  ) => void;
}

const getListItemStyle = (hasChildren: boolean, level: number) =>
  hasChildren
    ? { paddingLeft: `${level}rem`, paddingRight: '1rem' }
    : { paddingLeft: `${level + 2}rem`, paddingRight: '1rem' };

const styles = {
  toggle: {
    cursor: 'pointer',
    marginRight: '0.5rem',
  },
};

const UserGroupTree = ({
  userGroupTree,
  selected,
  onToggle,
  handleToggleDescendants,
}: Props) => {
  const [open, setOpen] = useState<Set<string>>(new Set(['root']));
  const [hoverId, setHoverId] = useState<string>();
  const [selectAll, setSelectAll] = useState<boolean>(true);

  const handleToggleSetDescendent = () => {
    handleToggleDescendants(userGroupTree, selectAll);
    setSelectAll(!selectAll);
  };

  const hasChildren = !!userGroupTree?.userGroups?.length;
  const hovering = hoverId === 'root';
  const showDescendantToggle = hovering && hasChildren;

  return (
    <div key={userGroupTree?.id}>
      <ListItemButton
        disableGutters
        style={getListItemStyle(hasChildren, 0)}
        onMouseEnter={() => setHoverId(userGroupTree?.id)}
        onMouseLeave={() => setHoverId(null)}
      >
        {hasChildren && (
          <Icon
            style={styles.toggle}
            onClick={() => setOpen(toggleFromSet(open, userGroupTree.id))}
          >
            {open.has(userGroupTree?.id) ? 'arrow_drop_down' : 'arrow_right'}
          </Icon>
        )}
        <ListItemText primary={userGroupTree?.name} />
        {showDescendantToggle && (
          <div
            className={classes.toggleButton}
            onClick={handleToggleSetDescendent}
          >
            {`${selectAll ? 'Select' : 'Unselect'} All Sub Groups`}
          </div>
        )}
      </ListItemButton>
      {hasChildren && (
        <Collapse in={open.has(userGroupTree?.id)} timeout="auto" unmountOnExit>
          {userGroupTree?.userGroups.map(userGroupItem => (
            <UserGroupTreeNode
              userGroup={userGroupItem}
              selected={selected}
              onToggle={onToggle}
              handleToggleDescendants={handleToggleDescendants}
              open={open}
              setOpen={setOpen}
              level={1}
            />
          ))}
        </Collapse>
      )}
    </div>
  );
};

export default UserGroupTree;
